import { schoolUsersId, schoolViewPath } from "../../utils/schoolStaff";

// MIS report definitions: columns, search fields and filters per category, plus the
// pure helpers (search, filter, sort, export scope) shared by the page, the phone cards and exports.
//
// All three report APIs return the complete dataset in one response (the bus listing is
// requested with limit "0"), so search, filters, sorting and pagination run over every record.

export const REPORT_TABS = ["School", "Police Station", "Bus", "Bus Report", "Defaulter"];

// Validity dates within this many days are flagged, matching the Bus table's colours.
export const EXPIRY_WARNING_DAYS = 30;

// Ids arrive base64-encoded; the export shows the plain value, as the previous export did.
export const decodeId = (id) => {
  if (id === undefined || id === null || id === "") return "";
  try {
    return decodeURIComponent(
      atob(String(id))
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
  } catch (e) {
    return String(id);
  }
};

export const POLICE_STATION_ROLE = 8;

// Role ids arrive base64-encoded ("OA==" is 8); a plain 8 or "8" is accepted too.
export const roleNumber = (role) => {
  const value = String(role ?? "").trim();
  if (/^\d+$/.test(value)) return Number(value);
  const decoded = decodeId(value);
  return /^\d+$/.test(decoded) ? Number(decoded) : null;
};

// Police Station users don't get the Police Station report; every other role keeps all tabs.
export const reportTabsForRole = (role) =>
  roleNumber(role) === POLICE_STATION_ROLE ? REPORT_TABS.filter((t) => t !== "Police Station") : REPORT_TABS;

const MONTHS = /^(\d{4})-(\d{1,2})-(\d{1,2})|^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/;

// Accepts yyyy-mm-dd and dd-mm-yyyy (or dd/mm/yyyy); returns a local-midnight Date or null.
export const parseReportDate = (value) => {
  if (!value) return null;
  const match = String(value).trim().match(MONTHS);
  if (!match) return null;
  const [y, m, d] = match[1] ? [match[1], match[2], match[3]] : [match[6], match[5], match[4]];
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) || date.getFullYear() !== Number(y) || date.getMonth() !== Number(m) - 1 || date.getDate() !== Number(d) ? null : date;
};

export const daysUntil = (value, today = new Date()) => {
  const date = parseReportDate(value);
  if (!date) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((date - start) / 86400000);
};

// "expired" | "expiring" | "valid" | null (no readable date)
export const validityStatus = (value, today) => {
  const days = daysUntil(value, today);
  if (days === null) return null;
  if (days < 0) return "expired";
  if (days <= EXPIRY_WARNING_DAYS) return "expiring";
  return "valid";
};

const BUS_VALIDITY_FIELDS = ["fitness_valid_upto", "insurance_valid_upto", "puc_valid_upto", "permit_valid_upto"];

export const busValidity = (row, today) => {
  const statuses = BUS_VALIDITY_FIELDS.map((field) => validityStatus(row[field], today));
  if (statuses.includes("expired")) return "expired";
  if (statuses.includes("expiring")) return "expiring";
  if (statuses.every(status => status === "valid")) return "valid";
  return null;
};

// A bus is a defaulter when any of these documents' valid-upto date is before today (local calendar date).
export const DEFAULTER_DOCUMENTS = [
  { key: "fitness", field: "fitness_valid_upto", label: "Fitness" },
  { key: "insurance", field: "insurance_valid_upto", label: "Insurance" },
  { key: "puc", field: "puc_valid_upto", label: "PUC" },
  { key: "permit", field: "permit_valid_upto", label: "Permit" },
];
export const DEFAULTER_REASON_SEPARATOR = ", ";

export const defaulterReasons = (row, today) =>
  DEFAULTER_DOCUMENTS.filter((doc) => validityStatus(row[doc.field], today) === "expired").map((doc) => `${doc.label} Expired`);

// Validity fields whose document is expired. Defaulter rows are read from their own reason text,
// so exports colour exactly the dates the Defaulter Reason names; other rows fall back to today's date.
export const expiredDocumentFields = (row, today) => {
  const reasons = row.defaulter_reason ? String(row.defaulter_reason).split(DEFAULTER_REASON_SEPARATOR) : defaulterReasons(row, today);
  return DEFAULTER_DOCUMENTS.filter((doc) => reasons.includes(`${doc.label} Expired`)).map((doc) => doc.field);
};

// The bus listing has no defaulter flag, so defaulters are derived from the same records the Bus tab shows.
export const buildDefaulterRows = (buses = [], today = new Date()) =>
  buses.reduce((rows, bus) => {
    const reasons = defaulterReasons(bus, today);
    if (reasons.length) rows.push({ ...bus, defaulter_reason: reasons.join(DEFAULTER_REASON_SEPARATOR) });
    return rows;
  }, []);

const toCount = (value) => {
  const n = Number(value);
  return value === "" || value === null || value === undefined || Number.isNaN(n) ? null : n;
};

// Column types: id (kept as text), text, count (number), date (validity date).
const countColumns = [
  { field: "pending", header: "Pending", type: "count", status: "pending" },
  { field: "approve", header: "Approved", type: "count", status: "approved" },
  { field: "reject", header: "Rejected", type: "count", status: "rejected" },
];

const hasCountOptions = [
  { value: "pending", label: "Has pending records" },
  { value: "approved", label: "Has approved records" },
  { value: "rejected", label: "Has rejected records" },
];
const COUNT_FIELD = { pending: "pending", approved: "approve", rejected: "reject" };
const verificationFilter = {
  key: "verification",
  label: "Verification status",
  options: () => hasCountOptions,
  test: (row, value) => (toCount(row[COUNT_FIELD[value]]) || 0) > 0,
};

const distinctOptions = (rows, field) =>
  [...new Set(rows.map((row) => row[field]).filter((v) => v !== undefined && v !== null && String(v).trim() !== ""))]
    .map(String)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((value) => ({ value, label: value }));

export const REPORTS = {
  School: {
    title: "School",
    fileLabel: "School",
    noun: "schools",
    viewPath: "/school/view",
    viewUrl: schoolViewPath,
    viewState: (row) => ({ school: row }),
    // School Details reloads by the internal users_id (carried as school_id), never by register_no.
    recordId: schoolUsersId,
    searchFields: ["school", "register_no"],
    searchPlaceholder: "Search by school name or registration no",
    primaryField: "school",
    columns: [
      { field: "register_no", header: "Reg No", type: "id" },
      { field: "school", header: "School Name", type: "text", primary: true },
      ...countColumns,
    ],
    exportColumns: [
      { field: "school_id", header: "ID", type: "id", value: (row) => decodeId(row.school_id) },
      { field: "register_no", header: "Reg No", type: "id" },
      { field: "school", header: "School Name", type: "text" },
      { field: "total", header: "Total", type: "count" },
      ...countColumns,
    ],
    filters: [verificationFilter],
  },
  "Police Station": {
    title: "Police Station",
    fileLabel: "Police_Station",
    noun: "police stations",
    viewPath: "/policestation/view",
    viewState: (row) => ({ ps: row }),
    recordId: (row) => row.police_station_id,
    searchFields: ["police_station", "register_no"],
    searchPlaceholder: "Search by police station or registration no",
    primaryField: "police_station",
    columns: [
      { field: "register_no", header: "Reg No", type: "id" },
      { field: "police_station", header: "Police Station Name", type: "text", primary: true },
      ...countColumns,
    ],
    exportColumns: [
      { field: "police_station_id", header: "ID", type: "id", value: (row) => decodeId(row.police_station_id) },
      { field: "register_no", header: "Reg No", type: "id" },
      { field: "police_station", header: "Police Station", type: "text" },
      { field: "total", header: "Total", type: "count" },
      ...countColumns,
    ],
    filters: [verificationFilter],
  },
  Bus: {
    title: "Bus",
    fileLabel: "Bus",
    noun: "buses",
    viewPath: "/bus/view",
    viewState: (row) => ({ bus: row }),
    recordId: (row) => row.bus_id,
    searchFields: ["bus_number", "school", "owner_name", "vehicle_type"],
    searchPlaceholder: "Search by bus number, school, owner or vehicle type",
    primaryField: "bus_number",
    columns: [
      { field: "school", header: "School", type: "text" },
      { field: "bus_number", header: "Bus Number", type: "id", primary: true },
      { field: "owner_name", header: "Owner Name", type: "text" },
      { field: "fitness_valid_upto", header: "Fitness", type: "date", highlightExpired: true },
      { field: "insurance_valid_upto", header: "Insurance", type: "date", highlightExpired: true },
      { field: "puc_valid_upto", header: "PUC", type: "date", highlightExpired: true },
      { field: "permit_valid_upto", header: "Permit", type: "date", highlightExpired: true },
      { field: "vehicle_type", header: "Vehicle Type", type: "text" },
    ],
    // Only the on-screen fields; the full record (RC, numbers, insurer, declaration...) stays on Bus View.
    exportColumns: [
      { field: "school", header: "School Name", type: "text" },
      { field: "bus_number", header: "Bus Number", type: "id", bold: true },
      { field: "owner_name", header: "Owner Name", type: "text" },
      { field: "fitness_valid_upto", header: "Fitness Valid Upto", type: "date", highlightExpired: true },
      { field: "insurance_valid_upto", header: "Insurance Valid Upto", type: "date", highlightExpired: true },
      { field: "puc_valid_upto", header: "PUC Valid Upto", type: "date", highlightExpired: true },
      { field: "permit_valid_upto", header: "Permit Valid Upto", type: "date", highlightExpired: true },
      { field: "vehicle_type", header: "Vehicle Type", type: "text" },
    ],
    filters: [
      { key: "school", label: "School", options: (rows) => distinctOptions(rows, "school"), test: (row, value) => String(row.school ?? "") === value },
      { key: "vehicle_type", label: "Vehicle type", options: (rows) => distinctOptions(rows, "vehicle_type"), test: (row, value) => String(row.vehicle_type ?? "") === value },
      {
        key: "validity",
        label: "Document validity",
        options: () => [
          { value: "expired", label: "Any document expired" },
          { value: "expiring", label: `Expiring within ${EXPIRY_WARNING_DAYS} days` },
          { value: "valid", label: "All documents valid" },
        ],
        test: (row, value) => busValidity(row) === value,
      },
    ],
  },
  "Bus Report": {
    title: "Bus Report",
    fileLabel: "Bus_Summary",
    noun: "schools",
    // A row summarises one school, so it opens that school's buses; each bus opens Bus View like the Bus tab.
    opensBusList: true,
    recordId: (row) => row.school_id ?? row.school,
    searchFields: ["school"],
    searchPlaceholder: "Search by school name",
    primaryField: "school",
    columns: [
      { field: "school", header: "School Name", type: "text", primary: true },
      { field: "totalVehicle", header: "Total Vehicle", type: "count" },
    ],
    exportColumns: [
      { field: "school", header: "School", type: "text" },
      { field: "totalVehicle", header: "Total Vehicle", type: "count" },
    ],
    filters: [],
  },
  Defaulter: {
    title: "Defaulter Bus",
    fileLabel: "Defaulter_Bus",
    noun: "defaulter buses",
    viewPath: "/bus/view",
    viewState: ({ defaulter_reason, ...bus }) => ({ bus }),
    recordId: (row) => row.bus_id,
    searchFields: ["bus_number", "school", "owner_name"],
    searchPlaceholder: "Search by bus number, school or owner",
    primaryField: "bus_number",
    columns: [
      { field: "school", header: "School Name", type: "text" },
      { field: "bus_number", header: "Bus Number", type: "id", primary: true },
      { field: "owner_name", header: "Owner Name", type: "text" },
      { field: "fitness_valid_upto", header: "Fitness Valid Upto", type: "date", highlightExpired: true },
      { field: "insurance_valid_upto", header: "Insurance Valid Upto", type: "date", highlightExpired: true },
      { field: "puc_valid_upto", header: "PUC Valid Upto", type: "date", highlightExpired: true },
      { field: "permit_valid_upto", header: "Permit Valid Upto", type: "date", highlightExpired: true },
      { field: "defaulter_reason", header: "Defaulter Reason", type: "reasons" },
    ],
    exportColumns: [
      { field: "school", header: "School Name", type: "text" },
      { field: "bus_number", header: "Bus Number", type: "id", bold: true },
      { field: "owner_name", header: "Owner Name", type: "text" },
      { field: "fitness_valid_upto", header: "Fitness Valid Upto", type: "date", highlightExpired: true },
      { field: "insurance_valid_upto", header: "Insurance Valid Upto", type: "date", highlightExpired: true },
      { field: "puc_valid_upto", header: "PUC Valid Upto", type: "date", highlightExpired: true },
      { field: "permit_valid_upto", header: "Permit Valid Upto", type: "date", highlightExpired: true },
      { field: "defaulter_reason", header: "Defaulter Reason", type: "text" },
    ],
    filters: [
      {
        key: "reason",
        label: "Expired document",
        options: () => [
          ...DEFAULTER_DOCUMENTS.map((doc) => ({ value: doc.key, label: `${doc.label} expired` })),
          { value: "multiple", label: "Multiple documents expired" },
        ],
        test: (row, value) => {
          const reasons = defaulterReasons(row);
          if (value === "multiple") return reasons.length > 1;
          const doc = DEFAULTER_DOCUMENTS.find((d) => d.key === value);
          return Boolean(doc) && reasons.includes(`${doc.label} Expired`);
        },
      },
      { key: "school", label: "School", options: (rows) => distinctOptions(rows, "school"), test: (row, value) => String(row.school ?? "") === value },
    ],
  },
};

// The authorized bus records that belong to a school: by id when both carry one, else by name.
export const schoolBuses = (school, buses = []) =>
  buses.filter(bus => {
    if (bus.school_id != null && school.school_id != null) return String(bus.school_id) === String(school.school_id);
    return Boolean(bus.school && school.school && bus.school === school.school);
  });

// Count returned authorized bus records, never personnel verification totals.
export const buildBusReportRows = (schools = [], buses = []) =>
  schools.map((item, index) => ({
    id: item.school_id ?? index + 1,
    school_id: item.school_id,
    school: item.school,
    totalVehicle: schoolBuses(item, buses).length,
  }));

// What a report row opens, resolved from the identifier the details screen loads by. Returns null
// when the record has no usable id, so the page never opens an empty or another record instead.
export const recordTarget = (tab, row) => {
  const report = REPORTS[tab];
  const id = row && report?.recordId ? report.recordId(row) : null;
  if (id === undefined || id === null || String(id).trim() === "") return null;
  if (report.opensBusList) return { id: String(id), busList: true };
  const { __key, ...record } = row;
  return { id: String(id), path: report.viewUrl ? report.viewUrl(record) : report.viewPath, state: report.viewState(record) };
};

export const emptyFilters = (tab) => Object.fromEntries(REPORTS[tab].filters.map((f) => [f.key, ""]));

export const activeFilterKeys = (tab, filters = {}) => REPORTS[tab].filters.filter((f) => filters[f.key]).map((f) => f.key);

export const filterLabel = (tab, key, value, rows = []) => {
  const filter = REPORTS[tab].filters.find((f) => f.key === key);
  if (!filter) return value;
  const option = filter.options(rows).find((o) => o.value === value);
  return `${filter.label}: ${option ? option.label : value}`;
};

// Search runs only over the fields the selected report actually has.
export const filterReportRows = (rows = [], tab, { search = "", filters = {} } = {}) => {
  const report = REPORTS[tab];
  const query = search.trim().toLowerCase();
  const active = report.filters.filter((f) => filters[f.key]);
  return rows.filter((row) => {
    const matchesSearch = !query || report.searchFields.some((key) => String(row[key] ?? "").toLowerCase().includes(query));
    return matchesSearch && active.every((f) => f.test(row, filters[f.key]));
  });
};

const sortValue = (row, column) => {
  const value = row[column.field];
  if (column.type === "count") return toCount(value);
  if (column.type === "date") return parseReportDate(value)?.getTime() ?? null;
  return value === undefined || value === null || value === "" ? null : String(value);
};

// Sorting happens here (not inside the grid) so exports list rows in the order on screen.
export const sortReportRows = (rows = [], tab, sortModel = []) => {
  const [sort] = sortModel;
  const column = sort && REPORTS[tab].columns.find((c) => c.field === sort.field);
  if (!column) return rows;
  const direction = sort.sort === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, column);
    const bv = sortValue(b, column);
    if (av === null && bv === null) return 0;
    if (av === null) return 1; // blanks always last
    if (bv === null) return -1;
    const result = typeof av === "number" ? av - bv : av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
    return result * direction;
  });
};

export const EXPORT_SCOPES = ["page", "filtered", "all"];

// Rows for each export scope: the visible page, every matching record, or the whole category.
export const exportRowsForScope = (scope, { allRows = [], sortedRows = [], page = 0, pageSize = 10 }) => {
  if (scope === "page") return sortedRows.slice(page * pageSize, (page + 1) * pageSize);
  if (scope === "filtered") return sortedRows;
  return allRows;
};
